import { useEffect, useState } from 'react';

import {
    AgreementsPermissionsV2,
    AssetsPermissionsV2,
    CasesPermissionsV2,
    HomePermissionsV2,
    SitesPermissionsV2,
    ResourcesPermissionsV2,
} from '../../../../constants/PermissionsV2';
import {
    fetchPermissionsV2,
    permissionsQueryClient,
} from '../../../../services/permissions/permissionsV2.service';
import { PermissionDtoV2, PermissionSetDtoV2 } from '../../../../services/permissions/permissionsV2.types';

export interface SectionPermissions {

    canViewAgreements:    boolean
    canViewMyCases:       boolean
    canViewCompanyCases:  boolean
    canViewCreateCase:    boolean
    canViewCaseViewList: boolean
    canViewCasesDetails: boolean
    canViewCaseResolution: boolean
    canViewAssetViewDetailsDocuments:boolean
    canViewAssetViewList:          boolean
    canViewAssetExportList:        boolean
    canViewAssetReportDataIssue:    boolean
    canViewAssetsViewDetails:  boolean
    canViewAgreementsViewList: boolean
    canViewAgreementsExportDetails: boolean
    canViewAgreementsViewContacts: boolean
    canViewAgreementsViewRelatedCases: boolean
    canViewAgreementsViewRelatedSites: boolean
    canViewAgreementsViewDetails: boolean
    canViewSitesView:boolean
    canViewSitesViewAssetsTree:boolean
    canViewSitesExportList:boolean
    canViewSitesExportDetails:boolean
    canViewSitesEditContact:boolean
    canViewSitesReportDataIssue:boolean
    canViewQuickAccessTiles:boolean
    canViewCSPQuickAccessTile:boolean
    canViewHEPowerShopQuickAccessTile:boolean
    canViewPSAQuickAccessTile:boolean
    canViewCybersecurityViewPatchFiles:boolean
}

const DENIED: SectionPermissions = {
    canViewAgreements:    false,
    canViewMyCases:       false,
    canViewCompanyCases:  false,
    canViewCreateCase:    false,
    canViewCasesDetails:  false,
    canViewAssetViewDetailsDocuments:false,
    canViewAssetViewList:         false,
    canViewAssetExportList:        false,
    canViewAssetReportDataIssue:    false,
    canViewAssetsViewDetails:        false,
    canViewCaseViewList:false,
    canViewCaseResolution:false,
    canViewAgreementsViewList: false,
    canViewAgreementsExportDetails: false,
    canViewAgreementsViewContacts: false,
    canViewAgreementsViewRelatedCases: false,
    canViewAgreementsViewRelatedSites: false,
    canViewAgreementsViewDetails: false,
    canViewSitesView:false,
    canViewSitesViewAssetsTree:false,
    canViewSitesExportList:false,
    canViewSitesExportDetails:false,
    canViewSitesEditContact:false,
    canViewSitesReportDataIssue:false,
    canViewQuickAccessTiles:false,
    canViewCSPQuickAccessTile:false,
    canViewHEPowerShopQuickAccessTile:false,
    canViewPSAQuickAccessTile:false,
    canViewCybersecurityViewPatchFiles:false,
}

const derivePermissions = (list: PermissionDtoV2[]): SectionPermissions => {
    const has = (name: string) => list.some(p => p.name === name)
    return {
        canViewAgreements:    has(AgreementsPermissionsV2.ViewDetails),
        canViewMyCases:       has(CasesPermissionsV2.CasesViewMyCases),
        canViewCompanyCases:  has(CasesPermissionsV2.CasesViewMyCompanyCases),
        canViewCreateCase:    has(AssetsPermissionsV2.CreateCase) || has(CasesPermissionsV2.Create),
        canViewCasesDetails:  has(CasesPermissionsV2.ViewDetails),
        canViewAssetViewDetailsDocuments: has(AssetsPermissionsV2.ViewDetailsDocuments),
        canViewAssetViewList: has(AssetsPermissionsV2.ViewList),
        canViewAssetExportList: has(AssetsPermissionsV2.ExportList),
        canViewAssetReportDataIssue: has(AssetsPermissionsV2.ReportDataIssue),
        canViewAssetsViewDetails:        has(AssetsPermissionsV2.ViewDetails),
        canViewCaseViewList:has(CasesPermissionsV2.ViewList),
        canViewCaseResolution: has(CasesPermissionsV2.Resolution),
        canViewAgreementsViewList: has(AgreementsPermissionsV2.ViewList),
        canViewAgreementsExportDetails: has(AgreementsPermissionsV2.ExportDetails),
        canViewAgreementsViewContacts: has(AgreementsPermissionsV2.ViewContacts),
        canViewAgreementsViewRelatedCases: has(AgreementsPermissionsV2.ViewRelatedCases),
        canViewAgreementsViewRelatedSites: has(AgreementsPermissionsV2.ViewRelatedSites),
        canViewAgreementsViewDetails: has(AgreementsPermissionsV2.ViewDetails),
        canViewSitesView: has(SitesPermissionsV2.View),
        canViewSitesViewAssetsTree: has(SitesPermissionsV2.ViewAssetsTree),
        canViewSitesExportList: has(SitesPermissionsV2.ExportList),
        canViewSitesExportDetails: has(SitesPermissionsV2.ExportDetails),
        canViewSitesEditContact: has(SitesPermissionsV2.EditContact),
        canViewSitesReportDataIssue: has(SitesPermissionsV2.ReportDataIssue),
        canViewCSPQuickAccessTile:has(HomePermissionsV2.ViewCSPQuickAccessTile),
        canViewHEPowerShopQuickAccessTile:has(HomePermissionsV2.ViewHEPowerShopQuickAccessTile),
        canViewPSAQuickAccessTile:has(HomePermissionsV2.ViewPSAQuickAccessTile),
        canViewQuickAccessTiles:has(HomePermissionsV2.ViewQuickAccessTiles),
        canViewCybersecurityViewPatchFiles:has(ResourcesPermissionsV2.CybersecurityViewPatchFiles)
    }
}

export interface UseSectionPermissionsResult {
    permissions: SectionPermissions
    isLoading:   boolean
}

export const useSectionPermissions = (): UseSectionPermissionsResult => {
    const customerId = sessionStorage.getItem('cop:customer-id') ?? ''

    const [permissions, setPermissions] = useState<SectionPermissions>(() => {
        // Synchronously read from pre-warmed cache on first render.
        // Cache warm  → correct permissions immediately, no false flicker.
        // Cache cold  → falls back to DENIED; useEffect below fills it.
        const cached = permissionsQueryClient.getQueryData<PermissionSetDtoV2>(['PermissionsV2', customerId]);
        return cached ? derivePermissions(cached.permissions) : DENIED;
    })

    const [isLoading, setIsLoading] = useState<boolean>(() => {
        // Not loading if cache already has data
        const cached = permissionsQueryClient.getQueryData<PermissionSetDtoV2>(['PermissionsV2', customerId]);
        return !cached;
    })

    useEffect(() => {
        if (!customerId) {
            setIsLoading(false)
            return
        }
        fetchPermissionsV2(customerId)
            .then(data => {
                setPermissions(derivePermissions(data.permissions))  // ONE setState → ONE re-render
            })
            .catch(() => {
                setPermissions(DENIED)
            })
            .finally(() => {
                setIsLoading(false)
            })
    }, [customerId])

    return { permissions, isLoading }
}